// src/components/PropertyCard.jsx
import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import {
  FaBath,
  FaCar,
  FaBed,
  FaRuler,
  FaHome,
  FaEllipsisV,
  FaExchangeAlt,
  FaArrowUp,
  FaEdit,
  FaTrash,
  FaMapSigns,
  FaStar,
} from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Selo from './Selo';
import BotaoComparar from './BotaoComparar';
import '../styles/tooltip.css';

import EncerrarAnuncioModal from './imoveis/EncerrarAnuncioModal';
import {
  encerrarAnuncio,
  togglePlacaImovel,
  toggleDestaqueImovel,
} from '../services/imoveisService';
import { criarVenda } from '../services/vendasService';
import { toast } from 'react-toastify';

const yes = (v) =>
  v === true || (typeof v === 'string' && v.toLowerCase() === 'sim');

const isHttpUrl = (u) => typeof u === 'string' && /^https?:\/\//i.test(u);
const isRenderable = (u) =>
  typeof u === 'string' &&
  /\.(jpe?g|png|webp|gif|avif)$/i.test(u.split('?')[0]);

const firstUrlFromArray = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return '';
  const urls = arr
    .map((x) => (typeof x === 'string' ? x : x?.url))
    .filter(Boolean);
  const ok = urls.find((u) => isHttpUrl(u) && isRenderable(u));
  return ok || '';
};

// ✅ NOVO: onUpdateLocal para atualizar o array do pai (evita “desmarcar”)
const PropertyCard = ({ property, onUpdateLocal }) => {
  const { usuario, loadingUserDoc } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const [openEncerrar, setOpenEncerrar] = useState(false);
  const [loadingEncerrar, setLoadingEncerrar] = useState(false);

  if (!property || loadingUserDoc) return null;

  const cardId = property.id || property.objectID || null;
  const canRoute = Boolean(cardId);

  // ✅ dono (compat legado)
  const donoId =
    property.ownerId ||
    property.usuarioId ||
    property.criadoPorUid ||
    property.criadoPor?.uid ||
    property.usuario?.uid ||
    null;

  const isMyProperty = donoId === usuario?.uid;

  // ✅ status: se estiver "encerrado" ou statusNegocio != ativo
  const statusNegocio = property.statusNegocio || 'ativo';
  const statusDoc = property.status || 'publicado';
  const isEncerrado = statusDoc === 'encerrado' || statusNegocio !== 'ativo';

  const [temPlaca, setTemPlaca] = useState(!!property.temPlaca);
  const [loadingPlaca, setLoadingPlaca] = useState(false);

  // ✅ destaque
  const [destaque, setDestaque] = useState(!!property.destaque);
  const [loadingDestaque, setLoadingDestaque] = useState(false);

  useEffect(() => {
    setTemPlaca(!!property.temPlaca);
  }, [property.temPlaca]);

  useEffect(() => {
    setDestaque(!!property.destaque);
  }, [property.destaque]);

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const handleTogglePlaca = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isMyProperty || loadingPlaca || !cardId) return;

    try {
      setLoadingPlaca(true);
      const novoValor = !temPlaca;

      // ✅ otimista (UI)
      setTemPlaca(novoValor);
      await togglePlacaImovel(cardId, novoValor);

      toast.success(
        novoValor ? 'Imóvel marcado como COM PLACA' : 'Placa removida'
      );
    } catch (err) {
      setTemPlaca((v) => !v);
      toast.error('Erro ao atualizar placa');
    } finally {
      setLoadingPlaca(false);
    }
  };

  // ✅ toggle destaque (máx 6) sem “piscar”
  const handleToggleDestaque = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isMyProperty || loadingDestaque || !cardId) return;

    const novoValor = !destaque;

    try {
      setLoadingDestaque(true);

      // ✅ otimista (UI + Pai) — evita re-render resetar o destaque
      setDestaque(novoValor);
      onUpdateLocal?.(cardId, { destaque: novoValor });

      await toggleDestaqueImovel(cardId, novoValor, usuario?.uid, 6);

      toast.success(
        novoValor ? 'Imóvel marcado como DESTAQUE' : 'Destaque removido'
      );
    } catch (err) {
      // rollback
      setDestaque((v) => !v);
      onUpdateLocal?.(cardId, { destaque: !novoValor });

      toast.error(err?.message || 'Erro ao atualizar destaque');
    } finally {
      setLoadingDestaque(false);
    }
  };

  const handleEncerrarAnuncio = () => {
    setMenuOpen(false);

    if (!isMyProperty) {
      toast.info('Apenas o criador pode encerrar este anúncio.');
      return;
    }
    if (!canRoute) {
      toast.error('ID do imóvel ausente.');
      return;
    }
    setOpenEncerrar(true);
  };

  const handleEncerrarConfirm = async (payload) => {
    if (!cardId) {
      toast.error('ID do imóvel não encontrado.');
      return;
    }

    try {
      setLoadingEncerrar(true);

      await encerrarAnuncio(cardId, payload);

      // ✅ redundante, mas ok manter se você ainda usa vendasService em algum lugar
      if (payload.resultado === 'vendido' && payload.venda) {
        if (usuario?.uid) {
          await criarVenda(usuario.uid, {
            uid: usuario.uid,
            corretorId: usuario.uid,
            data: payload.venda.data,
            clienteNome: payload.venda.clienteNome,
            produtoDescricao: payload.venda.produtoDescricao,
            valor: payload.venda.valor,
            comissao: payload.venda.comissao,
            imovelId: cardId,
            origem: 'anuncio',
          });
        }
      }

      toast.success('Anúncio encerrado com sucesso.');
      setOpenEncerrar(false);
    } catch (err) {
      console.error('Erro ao encerrar anúncio:', err);
      toast.error('Não foi possível encerrar o anúncio. Tente novamente.');
    } finally {
      setLoadingEncerrar(false);
    }
  };

  // ✅ imagem robusta
  const imgSrc =
    (isHttpUrl(property.thumbnail) &&
      isRenderable(property.thumbnail) &&
      property.thumbnail) ||
    (isRenderable(property.thumbnail?.url) && property.thumbnail?.url) ||
    (isHttpUrl(property.capaUrl) &&
      isRenderable(property.capaUrl) &&
      property.capaUrl) ||
    firstUrlFromArray(property.imagens) ||
    firstUrlFromArray(property.images) ||
    (isHttpUrl(property.cover) &&
      isRenderable(property.cover) &&
      property.cover) ||
    (isHttpUrl(property.image) &&
      isRenderable(property.image) &&
      property.image) ||
    (isHttpUrl(property.img) && isRenderable(property.img) && property.img) ||
    '/placeholder.png';

  const preco = Number(property.preco || 0);
  const areaTerreno = Number(property.areaTerreno || 0);
  const areaConstruida = Number(property.areaConstruida || 0);
  const dormitorios = Number(property.dormitorios || 0);
  const banheiros = Number(property.banheiros || 0);
  const vagas = Number(property.vagas || 0);

  // selos
  const isUpgrade = yes(property.aceleradorVendas);
  const isPermuta =
    !!property.permuta?.aceita ||
    yes(property.aceitaPermuta) ||
    yes(property.aceitaPermutaVeiculos) ||
    yes(property.aceitaPermutaImoveis);

  const badges = useMemo(() => {
    if (Array.isArray(property.badges) && property.badges.length)
      return property.badges;

    const b = [];
    if (isUpgrade) b.push({ key: 'upgrade', label: 'Upgrade' });
    if (isPermuta) b.push({ key: 'permuta', label: 'Aceita permuta' });
    return b;
  }, [property.badges, isUpgrade, isPermuta]);

  const permutaResumo =
    property.permutaResumo ||
    (property.permuta?.aceita ? 'Aceita permuta (detalhes no cadastro).' : '');

  const CardBody = (
    <>
      <div className="relative w-full aspect-[4/3] bg-gray-100 overflow-hidden">
        <img
          src={imgSrc}
          alt={`Imóvel${property.bairro ? ` no ${property.bairro}` : ''}${
            property.cidade ? `, ${property.cidade}` : ''
          }`}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-300"
          onLoad={(e) => (e.currentTarget.style.opacity = 1)}
          onError={(e) => {
            if (e.currentTarget.src.endsWith('/placeholder.png')) return;
            e.currentTarget.src = '/placeholder.png';
          }}
        />

        {/* ✅ Botão Destaque */}
        {isMyProperty && (
          <button
            type="button"
            onClick={handleToggleDestaque}
            title={destaque ? 'Remover destaque' : 'Marcar como destaque'}
            className={`
              absolute top-2 right-2 z-30
              p-2 rounded-full
              transition-all duration-200
              ${
                destaque
                  ? 'bg-amber-500 text-white shadow-lg'
                  : 'bg-white text-gray-400 hover:text-amber-600'
              }
              ${loadingDestaque ? 'opacity-70 cursor-wait' : ''}
            `}
            aria-label={
              destaque
                ? 'Remover destaque do imóvel'
                : 'Marcar imóvel como destaque'
            }
            disabled={loadingDestaque}
          >
            <FaStar size={16} />
          </button>
        )}

        {/* ✅ Botão Placa */}
        {isMyProperty && (
          <button
            type="button"
            onClick={handleTogglePlaca}
            title={temPlaca ? 'Remover placa' : 'Marcar como com placa'}
            className={`
              absolute top-2 right-12 z-30
              p-2 rounded-full
              transition-all duration-200
              ${
                temPlaca
                  ? 'bg-emerald-500 text-white shadow-lg'
                  : 'bg-white text-gray-400 hover:text-emerald-500'
              }
              ${loadingPlaca ? 'opacity-70 cursor-wait' : ''}
            `}
            aria-label={
              temPlaca ? 'Remover placa do imóvel' : 'Marcar imóvel com placa'
            }
            disabled={loadingPlaca}
          >
            <FaMapSigns size={16} />
          </button>
        )}

        {isEncerrado && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-20">
            <span className="px-3 py-1 rounded-full bg-white/90 text-xs font-semibold text-gray-800">
              {statusNegocio === 'vendido'
                ? 'Vendido'
                : statusNegocio === 'perdido'
                  ? 'Venda perdida'
                  : statusNegocio === 'retirado'
                    ? 'Retirado do ar'
                    : 'Anúncio encerrado'}
            </span>
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span className="bg-blue-700 text-white px-2 py-0.5 rounded font-medium uppercase">
            {property.tipo || 'Imóvel'}
          </span>
          <span>Ref.: {property.referencia || property.refCodigo || '--'}</span>
        </div>

        <h3 className="text-base font-semibold text-gray-800 line-clamp-1">
          {property.bairro || property.titulo || '--'}
        </h3>

        <p className="text-sm text-gray-600">
          {property.cidade || '--'}{' '}
          <span className="text-black font-bold">
            {preco > 0 ? `| R$ ${preco.toLocaleString('pt-BR')}` : '| R$ --'}
          </span>
        </p>

        {badges?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {badges.map((b) => (
              <span
                key={b.key}
                className="px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200"
              >
                {b.label}
              </span>
            ))}
          </div>
        )}

        {permutaResumo && isPermuta && (
          <p className="mt-2 text-xs text-gray-500 line-clamp-2">
            {permutaResumo}
          </p>
        )}

        <div className="flex flex-wrap gap-3 mt-3 text-sm text-blue-700">
          {areaTerreno > 0 && (
            <div className="flex items-center gap-1">
              <FaRuler /> {areaTerreno}m²
            </div>
          )}
          {areaConstruida > 0 && (
            <div className="flex items-center gap-1">
              <FaHome /> {areaConstruida}m²
            </div>
          )}
          {dormitorios > 0 && (
            <div className="flex items-center gap-1">
              <FaBed /> {dormitorios}
            </div>
          )}
          {banheiros > 0 && (
            <div className="flex items-center gap-1">
              <FaBath /> {banheiros}
            </div>
          )}
          {vagas > 0 && (
            <div className="flex items-center gap-1">
              <FaCar /> {vagas}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition relative">
        {/* ✅ MENU (3 pontinhos) */}
        {isMyProperty && (
          <div className="absolute top-2 right-24 z-40" ref={dropdownRef}>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenuOpen((o) => !o);
              }}
              className="
                p-2 rounded-full bg-white/95 shadow
                text-gray-600 hover:text-gray-900
                focus:outline-none
              "
              aria-label="Abrir menu do imóvel"
              title="Opções"
            >
              <FaEllipsisV />
            </button>

            {menuOpen && (
              <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg text-sm z-50 overflow-hidden">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMenuOpen(false);
                    if (canRoute) navigate(`/editar-imovel/${cardId}`);
                  }}
                  className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-gray-50 text-gray-700"
                >
                  <FaEdit /> Editar
                </button>

                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleEncerrarAnuncio();
                  }}
                  disabled={isEncerrado}
                  className={`flex items-center gap-2 w-full text-left px-4 py-2 ${
                    isEncerrado
                      ? 'text-zinc-400 cursor-not-allowed bg-white'
                      : 'hover:bg-amber-50 text-amber-700'
                  }`}
                >
                  <FaTrash className="opacity-80" />
                  {isEncerrado ? 'Anúncio encerrado' : 'Encerrar anúncio'}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="absolute top-2 left-2 flex gap-2 z-20">
          {isUpgrade && (
            <div className="relative group cursor-pointer">
              <Selo icon={<FaArrowUp />} texto="Upgrade" cor="green" />
              <div className="tooltip-custom">
                O dono procura um imóvel maior/melhor.
              </div>
            </div>
          )}
          {isPermuta && (
            <div className="relative group cursor-pointer">
              <Selo icon={<FaExchangeAlt />} texto="Permuta" cor="blue" />
              <div className="tooltip-custom">
                {property.permutaResumo
                  ? property.permutaResumo
                  : 'Aceita permuta.'}
              </div>
            </div>
          )}
        </div>

        {canRoute ? (
          <Link
            to={`/imovel/${cardId}`}
            className="block text-inherit hover:no-underline"
          >
            {CardBody}
          </Link>
        ) : (
          <div className="block">{CardBody}</div>
        )}

        <div className="flex justify-between items-center px-4 pb-4 mt-2">
          <span className="text-sm text-black">
            {property.origem === 'meu' ? (
              <strong>
                Por: {property.ownerNome || usuario?.nome || 'Você'}
              </strong>
            ) : property.origem === 'socio' ? (
              <>Sócio(a): {property.ownerNome || 'Corretor(a)'}</>
            ) : property.origem === 'parceiro' ? (
              <>Parceiro(a): {property.ownerNome || 'Corretor(a)'}</>
            ) : (
              `Por: ${property.ownerNome || 'Corretor(a)'}`
            )}
          </span>
          <BotaoComparar id={cardId || ''} />
        </div>
      </div>

      <EncerrarAnuncioModal
        open={openEncerrar}
        onClose={() => !loadingEncerrar && setOpenEncerrar(false)}
        imovel={property}
        onConfirm={handleEncerrarConfirm}
        loading={loadingEncerrar}
      />
    </>
  );
};

export default PropertyCard;
